import { Component, OnInit } from '@angular/core';
import { DataSource, DataSourceService, FeatureMotion, IgoMap, LayerOptions, LayerService, MapBrowserComponent, OSMDataSource, OSMDataSourceOptions, QueryableDataSourceOptions, VectorLayer, moveToOlFeatures } from '@igo2/geo';
import { Feature } from 'ol';
import { Circle, Point } from 'ol/geom';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FicheDescriptiveTableComponent } from './fiche-descriptive-table/fiche-descriptive-table.component';
import * as olstyle from 'ol/style';
import olClusterSource from 'ol/source/Cluster';
import olVectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import type { default as OlGeometry } from 'ol/geom/Geometry';
import { StorageService } from '@igo2/core/storage';

export interface FicheDescriptiveOptions {
  requestType?: string // "wfs"|"arcgisrest"|"unsupported",
  url: string,
  params: any,
  title?: string,
  isLoaded: boolean
}

@Component({
    selector: 'app-fiche-descriptive',
    templateUrl: './fiche-descriptive.component.html',
    styleUrls: ['./fiche-descriptive.component.scss'],
    imports: [
        CommonModule,
        FicheDescriptiveTableComponent,
        MapBrowserComponent
    ]
})
export class FicheDescriptiveComponent implements OnInit {
  public map: IgoMap = new IgoMap({
    controls: {
      attribution: {
        collapsed: true
      },
      scaleLine: true
    }
  });
  private baseUrl: string;

  public defaultStyle: olstyle.Style | ((feature, resolution) => olstyle.Style);
  public listeDesTables: FicheDescriptiveOptions[] = [];
  public isLoading: boolean = false;
  private selectedMapZoneJson: any;
  private selectedMapZoneGeom: OlGeometry;
  private mapProjection: string;
  nbCouches: number;
  totalCouches: number;

  constructor(
    private configService: ZzzConfigService,
    private dataSourceService: DataSourceService,
    private layerService: LayerService,
    private storageService: StorageService
  ) {
  }

  ngOnInit(): void {
    this.baseUrl = this.configService.get().authApiEndPoint;
    this.mapProjection = this.map.ol.getView().getProjection().getCode();
    this.createMap();
    this.buildTables();
  }

  createMap() {
    var featureFromStore = this.storageService.get('sigtSpatialFilter_zone');
    if (featureFromStore) {
      this.dataSourceService
        .createAsyncDataSource({
          type: 'osm'
        } satisfies OSMDataSourceOptions)
        .subscribe((dataSource: OSMDataSource) => {
          this.map.layerController.add(
            this.layerService.createLayer({
              title: 'OSM',
              source: dataSource,
              visible: true,
              baseLayer: true
            } satisfies LayerOptions)
          );
          this.loadSelectedMapZone();
        });
    }
  }

  loadSelectedMapZone() {
    var featureFromStore = this.storageService.get('sigtSpatialFilter_zone');
    if (featureFromStore) {
      const geoJson = new GeoJSON();
      this.selectedMapZoneJson = JSON.parse(featureFromStore.toString());
      switch (this.selectedMapZoneJson.type) {
        case 'Feature': {
          this.selectedMapZoneGeom = (geoJson.readFeature(this.selectedMapZoneJson) as Feature<OlGeometry>).getGeometry();
          //l'outil GeometryFormFieldInputComponent.setOlGeometry() d'IGO retourne une géometrie dans la projection 4326
          this.selectedMapZoneGeom.transform('EPSG:4326', this.map.ol.getView().getProjection());
          break;
        }
        case 'MultiPolygon':
        case 'Polygon': {
          this.selectedMapZoneGeom = geoJson.readGeometry(this.selectedMapZoneJson);
          //l'outil GeometryFormFieldInputComponent.setOlGeometry() d'IGO retourne une géometrie dans la projection 4326
          this.selectedMapZoneGeom.transform('EPSG:4326', this.map.ol.getView().getProjection());
          break;
        }
        case 'Point': if (this.selectedMapZoneJson.radius) {
          var point = geoJson.readGeometry(this.selectedMapZoneJson) as Point;
          point.transform('EPSG:4326', this.map.ol.getView().getProjection());
          this.selectedMapZoneGeom = new Circle(point.getCoordinates(), this.selectedMapZoneJson.radius);
          break;
        }
        default: throw ("Type de feature inconnu");
      }

      this.map.ol.getView().fit(this.selectedMapZoneGeom.getExtent());
      const olFeature = new Feature({
        geometry: this.selectedMapZoneGeom
      });
      this.tryAddFeaturesToMap([olFeature]);
      moveToOlFeatures(
        this.map.viewController,
        [olFeature],
        FeatureMotion.Zoom
      );
    }
  }

  public tryAddFeaturesToMap(features: Feature[]) {
    this.defaultStyle = (_feature, _resolution) => {
      return new olstyle.Style({
        stroke: new olstyle.Stroke({
          width: 1,
          color: 'orange'
        }),
        fill: new olstyle.Fill({
          color: 'rgba(200, 200, 20, 0.2)'
        })
      });
    };

    this.dataSourceService
      .createAsyncDataSource({
        type: 'vector',
        queryable: true
      } as QueryableDataSourceOptions)
      .pipe(take(1))
      .subscribe((dataSource: DataSource) => {
        const olLayer = this.layerService.createLayer({
          isIgoInternalLayer: true,
          title: 'Zone',
          source: dataSource,
          visible: true
        }) as VectorLayer;

          const ol = dataSource.ol as olVectorSource | olClusterSource;
        ol.addFeatures(features);
        olLayer.ol.setStyle(this.defaultStyle);
        this.map.layerController.add(olLayer);
      });
  }

  buildTables() {
    //read local storage for WFS services to query
    var featureRequestsCount = this.storageService.get('sigtSpatialFilter_featureRequestsCount') as number ?? 0;
    this.totalCouches = featureRequestsCount;
    this.nbCouches = 0;
    if (featureRequestsCount > 0) {
      var requestIds = this.extractFeatureRequestTypeIds();
      if (requestIds.length !== this.totalCouches) {
        console.error("Invalid number of request found in local storage");
        return;
      }
      this.isLoading = true;
      for (var i = 0; i < featureRequestsCount; i++) {
        var id = requestIds[i];
        if (this.storageService.get(`sigtSpatialFilter_featureRequestUrl${id}`) === "") {
          this.listeDesTables.push({
            requestType: "unsupported",
            title: this.storageService.get(`sigtSpatialFilter_featureRequestTitle${id}`) as string,
            url: null,
            params: null,
            isLoaded: true
          });
        } else {
          this.listeDesTables.push({
            requestType: this.storageService.get(`sigtSpatialFilter_featureRequestType${id}`) as string,
            url: this.storageService.get(`sigtSpatialFilter_featureRequestUrl${id}`) as string,
            params: this.storageService.get(`sigtSpatialFilter_featureRequestParams${id}`) as string,
            title: this.storageService.get(`sigtSpatialFilter_featureRequestTitle${id}`) as string,
            isLoaded: false
          });
          this.nbCouches++;
        }
      }
    }
  }

  onDataLoaded() {
    this.nbCouches--;
    if (this.nbCouches === 0)
      this.isLoading = false;
  }

 extractFeatureRequestTypeIds(): number[] {
  const ids: number[] = [];
  const pattern = /^igo\.sigtSpatialFilter_featureRequestTitle(\d+)$/;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const match = key.match(pattern);
      if (match && match[1]) {
        ids.push(+match[1]);
      }
    }
  }

  return ids.sort();
}

}
